import {
  baseballLogicService,
  CallTypes,
  constants,
  IApiCatcherThrowTag,
  IApiGameState,
  PitchTypes,
  ResultTypes,
  TopBottom,
} from '@trackman/baseball-shared-components';
import { SessionType } from 'enums/SessionType';
import { History } from 'history';
import { IArchiveSession } from 'interfaces/archive/IArchiveSession';
import { IApiBallFinal } from 'interfaces/broadcast/IApiBallFinal';
import { IApiArchiveSessionModel } from 'interfaces/session/IApiArchiveSessionModel';
import { IApiPlayWithBallFinals } from 'interfaces/session/IApiPlayWithBallFinals';
import { IGameScheduleReference } from 'interfaces/session/ISessionMode';
import { IApiSessionMetadataUpdated } from 'interfaces/tagging/ApiModels/IApiSessionMetadataUpdated';
import { IApiStadiumPlayModel } from 'interfaces/tagging/ApiModels/IApiStadiumPlayModel';
import { IInning } from 'interfaces/tagging/IInning';
import { ITag } from 'interfaces/tagging/ITag';
import { IApiScheduleResponse } from 'interfaces/team/IApiScheduleResponse';
import { ISelectOption } from 'interfaces/ui/ISelectOption';
import { AdjustedValue } from 'models/tagging/AdjustedValue';
import { Play } from 'models/tagging/Play';
import { PlayLineup } from 'models/tagging/PlayLineup';
import moment from 'moment';
import { renderReviewButton, renderStatus, renderTaggedIcon, renderVideoUploadStatus } from 'pages/Archives/renderers/Renderers';
import { CallOptions } from 'pages/Tagging/components/TaggingFlow/optionArrays/CallOptions';
import uuid from 'uuid';
import { IApiSessionTeam } from '../../../interfaces/tagging/ApiModels/IApiSessionMetadataUpdateRequest';
import { getBatterHandednessForTag, getNextInning, getPitcherHandednessForTag, isHomeTeamBatting } from '../baseballLogicHelpers';

const playToApiPlayModelMap = (play: Play, session: IApiSessionMetadataUpdated): IApiStadiumPlayModel => {
  const model = {
    id: play.id,
    playId: play.playId,
    sessionId: session.sessionId,
    taggerBehavior: playToApiTaggerBehaviorMap(play),
    players: {
      pitcher: {
        ...play.pitcher,
        pitchingHandedness: getPitcherHandednessForTag(play),
      },
      batter: {
        ...play.batter,
        battingHandedness: getBatterHandednessForTag(play),
      },
      catcher: play.catcher,
      firstBaseman: play.firstBaseman,
      secondBaseman: play.secondBaseman,
      thirdBaseman: play.thirdBaseman,
      centerFielder: play.centerFielder,
      leftFielder: play.leftFielder,
      rightFielder: play.rightFielder,
      shortStopper: play.shortStopper,
    },
    gameState: playToApiGameStateMap(play),
    pitchTag: playToApiPitchTagMap(play),
    hitTag: playToApiHitTagMap(play),
    catcherThrowTag: playToApiCatcherThrows(play),
    challengeTag: play.challengeTag,
    playResult: playToApiPlayResult(play),
    note: play.note,
    kOrBB: play.korbb,
    pitchingTeam: isHomeTeamBatting(play.isTopInning)
      ? teamWithLineupToTeamMap(session.awayTeam)
      : teamWithLineupToTeamMap(session.homeTeam),
    battingTeam: isHomeTeamBatting(play.isTopInning)
      ? teamWithLineupToTeamMap(session.homeTeam)
      : teamWithLineupToTeamMap(session.awayTeam),
    customMetadata: {
      isValidated: play.isValidated,
      isNoteResolved: play.isNoteResolved,
      taggingFlowState: play.flowStateMachine.exportData(),
    },
    trackStartTimeUtc: play.trackStartTimeUtc,
    playLineup: play.playLineup.toApiModel(),
    strikeZoneDecision: play.strikeZoneDecision,
    baseRunners: {
      firstBase: play.baseRunners.firstBase,
      secondBase: play.baseRunners.secondBase,
      thirdBase: play.baseRunners.thirdBase,
    },
    fieldingSequence: {
      fielder1: play.fieldingSequence[0],
      fielder2: play.fieldingSequence[1],
      fielder3: play.fieldingSequence[2],
    },
    trackStartTimeAutoGenerated: play.trackStartTimeAutoGenerated,
  } as IApiStadiumPlayModel;

  return model;
};

const playToApiPlayResult = (play: Play) => {
  return {
    outs: play.tag.outs,
    runs: play.tag.runs,
    result: play.tag.playResult ? play.tag.playResult : ResultTypes.Undefined,
  };
};
const apiPlayModelToPlayMap = (model: IApiStadiumPlayModel, ballFinals?: IApiBallFinal[]) => {
  const play = new Play(uuid());
  play.id = model.id;
  play.playId = model.playId;
  play.inningNumber = model.gameState.inning;
  play.isTopInning = isTopInning(model.gameState.topBottom);
  play.playSequenceNumber = model.taggerBehavior.playSequenceNumber;
  play.sequenceNumber = model.taggerBehavior.sequenceNumber;
  play.isWarmup = model.taggerBehavior.isWarmup;
  play.awayTeamScore = getAwayTeamScore(model);
  play.homeTeamScore = getHomeTeamScore(model);
  play.tag = getTag(model);
  play.balls = getBalls(model, play.tag);
  play.strikes = getStrikes(model, play.tag);
  play.outs = getOuts(model);
  play.pitcher = model.players.pitcher;
  play.batter = model.players.batter;
  play.catcher = model.players.catcher;
  play.firstBaseman = model.players.firstBaseman;
  play.secondBaseman = model.players.secondBaseman;
  play.thirdBaseman = model.players.thirdBaseman;
  play.shortStopper = model.players.shortStopper;
  play.leftFielder = model.players.leftFielder;
  play.centerFielder = model.players.centerFielder;
  play.rightFielder = model.players.rightFielder;
  play.isCreatedManually = model.taggerBehavior.manuallyAddedByTagger;
  play.isTagStored = true;
  play.isValidated = model.customMetadata.isValidated;
  play.pitchBallFinalTrackId = model.pitchTag.ballFinalTrackId;
  play.hitBallFinalTrackId = model.hitTag.ballFinalTrackId;
  play.throwBallFinalTrackId = model.catcherThrowTag?.ballFinalTrackId;
  play.isDeleted = model.taggerBehavior.isDeleted;
  play.korbb = model.kOrBB;
  play.pitchOfPA = model.taggerBehavior.pitchOfPA;
  play.pAofInning = model.taggerBehavior.pAofInning;
  play.note = model.note;
  play.tag.catcherThrows = model.catcherThrowTag ? model.catcherThrowTag : undefined;
  play.playLineup = new PlayLineup(model.playLineup.homeTeam, model.playLineup.awayTeam);
  play.ballFinals = ballFinals || [];
  play.flowStateMachine.importData(model.customMetadata.taggingFlowState);
  play.strikeZoneDecision = model.strikeZoneDecision;
  play.challengeTag = model.challengeTag;
  play.baseRunners = model.baseRunners;
  play.fieldingSequence = [model.fieldingSequence?.fielder1, model.fieldingSequence?.fielder2, model.fieldingSequence?.fielder3];
  return play;
};

const isTopInning = (topBottom: string) => topBottom.toLowerCase() === 'top';

const getAwayTeamScore = (model: IApiStadiumPlayModel) => {
  const result = new AdjustedValue();
  result.initial = model.gameState.awayTeamScore;
  if (isTopInning(model.gameState.topBottom)) {
    result.initial -= model.playResult.runs;
    result.fromTag = model.playResult.runs;
  }
  return result;
};

const getHomeTeamScore = (model: IApiStadiumPlayModel) => {
  const result = new AdjustedValue();
  result.initial = model.gameState.homeTeamScore;
  if (!isTopInning(model.gameState.topBottom)) {
    result.initial -= model.playResult.runs;
    result.fromTag = model.playResult.runs;
  }
  return result;
};

const getBalls = (model: IApiStadiumPlayModel, tag: ITag) => {
  const result = new AdjustedValue();
  result.fromTag = baseballLogicService.isBall(tag.pitchCall) ? 1 : 0;
  result.initial = model.gameState.balls;
  return result;
};

const getStrikes = (model: IApiStadiumPlayModel, tag: ITag) => {
  const result = new AdjustedValue();
  result.fromTag = baseballLogicService.isStrike(tag.pitchCall, model.gameState.strikes) ? 1 : 0;
  result.initial = model.gameState.strikes;
  return result;
};

const getOuts = (model: IApiStadiumPlayModel) => {
  const result = new AdjustedValue();
  result.fromTag = model.playResult.outs;
  result.initial = model.gameState.outs;
  return result;
};

const getTag = (model: IApiStadiumPlayModel) => {
  const callOption = CallOptions.find((x) => x.value === model.pitchTag.pitchCall);
  return {
    pitchType: model.pitchTag.pitchType,
    pitchCall: model.pitchTag.pitchCall,
    pitchCallDisplay: callOption && callOption.displayName,
    hitType: model.hitTag.hitType,
    outs: model.playResult.outs,
    runs: model.playResult.runs,
    playResult: model.playResult.result,
  } as ITag;
};

const playToApiCatcherThrows = (play: Play) => {
  return {
    ballFinalTrackId: play.throwBallFinalTrackId,
    fielder: play.tag.catcherThrows?.fielder || { ...constants.emptyPlayer },
    thrownPosition: play.tag.catcherThrows?.thrownPosition || constants.undefinedValue,
  } as IApiCatcherThrowTag;
};

const playToApiTaggerBehaviorMap = (play: Play) => ({
  manuallyAddedByTagger: play.isCreatedManually,
  isWarmup: play.isWarmup,
  playSequenceNumber: play.playSequenceNumber,
  sequenceNumber: play.sequenceNumber,
  isDeleted: play.isDeleted,
  pitchOfPA: play.pitchOfPA,
  pAofInning: play.pAofInning,
});

const playToApiGameStateMap = (play: Play) =>
  ({
    homeTeamScore: play.homeTeamScore.total,
    awayTeamScore: play.awayTeamScore.total,
    inning: play.inningNumber,
    topBottom: play.isTopInning ? TopBottom.Top : TopBottom.Bottom,
    balls: Math.max(play.balls.initial + play.balls.adjustedBy, 0),
    strikes: Math.max(play.strikes.initial + play.strikes.adjustedBy, 0),
    outs: Math.max(play.outs.initial + play.outs.adjustedBy, 0),
  } as IApiGameState);

const playToApiPitchTagMap = (play: Play) => ({
  ballFinalTrackId: play.pitchBallFinalTrackId,
  pitchType: play.tag.pitchType ? play.tag.pitchType : PitchTypes.Undefined,
  pitchCall: play.tag.pitchCall ? play.tag.pitchCall : CallTypes.Undefined,
  pitcherSet: constants.undefinedValue, // TODO: what is it?
});

const playToApiHitTagMap = (play: Play) => ({
  ballFinalTrackId: play.hitBallFinalTrackId,
  hitType: play.tag.hitType,
});

const apiPlaysToInningsMap = (plays: IApiPlayWithBallFinals[]) => {
  const innings: IInning[] = [];

  const addEmptyInnings = (targetNumber: number, targetIsTop: boolean) => {
    if (innings.length === 0) {
      return;
    }
    const isTarget = (inning: IInning) => inning.number === targetNumber && inning.isTop === targetIsTop;
    let lastInning = innings[innings.length - 1];
    while (!isTarget(lastInning)) {
      if (targetNumber > lastInning.number) {
        lastInning = getNextInning(lastInning.number, lastInning.isTop, true);
      } else if (targetNumber < lastInning.number) {
        lastInning = getNextInning(lastInning.number, lastInning.isTop, false);
      } else {
        // lastInning.number == targetNumber
        lastInning = getNextInning(lastInning.number, lastInning.isTop, !targetIsTop); // if target is top, then lastInning must be bottom -> move downwards (ordering is top < bottom)
      }
      if (!isTarget(lastInning)) {
        innings.push(lastInning);
      }
    }
  };
  let currentInning = {} as IInning;
  plays.sort((a, b) => b.play.taggerBehavior.sequenceNumber - a.play.taggerBehavior.sequenceNumber); // sort by sequence number descending
  for (let i = 0; i < plays.length; i++) {
    const play = apiPlayModelToPlayMap(plays[i].play, plays[i].ballFinals);
    if (play.inningNumber !== currentInning.number || play.isTopInning !== currentInning.isTop) {
      if (currentInning.number !== undefined) {
        addEmptyInnings(currentInning.number, currentInning.isTop);
        innings.push(currentInning);
      }
      currentInning = {
        number: play.inningNumber,
        isTop: play.isTopInning,
        playsReversed: [],
      };
    }
    currentInning.playsReversed.push(play);
  }
  if (currentInning.number !== undefined) {
    addEmptyInnings(currentInning.number, currentInning.isTop);
    innings.push(currentInning);
  }
  return innings;
};

const valuesToDropdownMap = (values: any[]) => {
  const dropdownValues: ISelectOption[] = [];
  values
    .filter((v) => !!v.id && !!v.name)
    .forEach((value) => {
      dropdownValues.push({
        value: value.id,
        label: value.name,
      });
    });
  return dropdownValues;
};

const apiScheduleGameToGameScheduleReferenceMap = (schedule: IApiScheduleResponse): IGameScheduleReference => {
  return {
    externalSessionId: schedule.externalSessionId,
    id: schedule.id,
    league: schedule.league,
    level: schedule.gameLevel,
    awayTeam: {
      id: schedule.awayTeam.id,
      name: schedule.awayTeam.teamName,
      shortName: schedule.awayTeam.shortName,
      externalId: schedule.awayTeam.externalId,
      externalIds: schedule.awayTeam.externalIds,
      organizationId: schedule.awayTeam.organizationId,
    },
    homeTeam: {
      id: schedule.homeTeam.id,
      name: schedule.homeTeam.teamName,
      shortName: schedule.homeTeam.shortName,
      externalId: schedule.homeTeam.externalId,
      externalIds: schedule.homeTeam.externalIds,
      organizationId: schedule.homeTeam.organizationId,
    },
    location: {
      venue: { id: schedule.venueId, name: schedule.venueName },
      field: {
        id: schedule.fieldId,
        name: schedule.fieldName,
        shortName: schedule.fieldShortName,
      },
    },
    state: schedule.state,
    expectedStartDateTimeUtc: schedule.expectedTime,
    expectedStartDateTimeLocal: schedule.localExpectedTime,
  };
};

const mapSessionsToArchiveSessions = (sessions: IApiArchiveSessionModel[], history: History): IArchiveSession[] => {
  return sessions.map((session: IApiArchiveSessionModel) => {
    const time = `${moment(session.sessionModeState.sessionStartedLocal).format('MM/DD')} @ ${moment(
      session.sessionModeState.sessionStartedLocal
    ).format('hh:mm A')}`;
    return {
      startTime: time,
      awayTeam: session.awayTeam?.name,
      homeTeam: session.homeTeam?.name,
      league: session.league?.name,
      level: session.level?.name,
      status: renderStatus(session.sessionTaggingState.state),
      videoUploadStatus: renderVideoUploadStatus(session.uploadState),
      reviewButton: renderReviewButton(
        session.sessionId,
        history,
        session.sessionTaggingState.state,
        session.sessionModeState.state,
        session.sessionType === SessionType.BattingPractice
      ),
      fieldName: session.field.shortName,
      isOperatorlessTaggingEnabled: renderTaggedIcon(session.isOperatorlessTaggingEnabled),
    } as IArchiveSession;
  });
};

const teamWithLineupToTeamMap = (teamWithLineup: IApiSessionTeam | null) => {
  if (!teamWithLineup) {
    return null;
  }
  const { lineup, ...team } = teamWithLineup;
  return team;
};

export default {
  playToApiPlayModelMap,
  apiPlaysToInningsMap,
  apiScheduleGameToGameScheduleReferenceMap,
  mapSessionsToArchiveSessions,
  apiPlayModelToPlayMap,
  valuesToDropdownMap,
  teamWithLineupToTeamMap,
};
